import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { SALES_ACTIVITY_URLS } from './routes';
import { SalesActivity } from './sales-activity';

import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { SalesToolHostService } from './salestool.host.service';
import {
  LoggedInUserInfoServiceInterface,
  LoggedInUserInfoServiceInterfaceToken,
} from '../interfaces/logged-in-user-info.service.interface';

export const DEFAULT_API_PAGE_SIZE = 25; // This is the default page size returned in the nextUrl in the LookupSalesActivityResponse

export interface RawSalesActivity {
  reactions: [];
  auto_created: boolean;
  origin_id: string;
  custom_action_name: string;
  salesperson_photo: string;
  campaign_id: string;
  like_count: number;
  campaign_end_date: string;
  last_edit_datetime: string;
  campaign_name: string;
  partner_id: string;
  closed_lost_reason: string;
  event_datetime: string;
  event_time_diff: number;
  market_id: string;
  salesperson_name: string;
  comments: [];
  product_price: number;
  company_name: string;
  widget_flag: false;
  opportunity_id: string;
  product_name: string;
  sales_record_id: string;
  audio_key: string;
  campaign_start_date: string;
  salesperson_last_name: string;
  campaign_drop_id: string;
  connected: false;
  previous_sales_status: string;
  salesperson_first_name: string;
  deleted_date: string;
  closed_lost_reason_name: string;
  salesperson_id: string;
  sales_status: string;
  notes: string;
  sales_person_action: string;
  account_group_id: string;
}

export interface Filters {
  salesperson?: string[];
  status?: string[];
  action?: string[];
  opportunityId?: string;
  accountGroupId?: string;
  fromDate?: string;
  toDate?: string;
  showActivities?: boolean;
  filteredSalespeople?: string[];
}

export interface LookupSalesActivityResponse {
  salesActivities: SalesActivity[];
  total: number;
  nextUrl: string;
}

export interface GetSalesActivityRequestBody {
  opportunityId?: string;
  accountGroupId?: string;
  partnerId: string;
  marketId?: string;
  currentSalespersonId?: string;
  salespersonId?: string[];
  accountStatus?: string[];
  actionTaken?: string[];
  fromDateWithTimezone?: string;
  toDateWithTimezone?: string;
}

@Injectable()
export class SalesActivityService {
  salesActivities$$: BehaviorSubject<LookupSalesActivityResponse> = new BehaviorSubject(null);
  readonly salesActivities$: Observable<LookupSalesActivityResponse> = this.salesActivities$$.asObservable();

  constructor(
    private readonly host: SalesToolHostService,
    private readonly _http: HttpClient,
    private readonly translate: TranslateService,
    @Inject(LoggedInUserInfoServiceInterfaceToken)
    private readonly loggedInUserInfoService: LoggedInUserInfoServiceInterface,
  ) {}

  loadSalesActivities(nextUrl: string, filters: Filters = {}): void {
    if (!filters.showActivities && Object.prototype.hasOwnProperty.call(filters, 'showActivities')) {
      const resp = {
        data: [],
        totalResults: 0,
        nextUrl: null,
      };
      this.processSalesActivitiesResponse(resp, null);
    } else {
      this._loadSalesActivitiesRequest(nextUrl, filters).subscribe((resp) =>
        this.processSalesActivitiesResponse(resp, nextUrl),
      );
    }
  }

  processSalesActivitiesResponse(resp: any, nextUrl: string): void {
    const activities = resp.data.map(this.deserializeActivity);
    const fullsalesActivityList = !nextUrl
      ? activities
      : this.salesActivities$$.getValue().salesActivities.concat(activities);
    this.salesActivities$$.next({
      salesActivities: fullsalesActivityList,
      total: resp.totalResults,
      nextUrl: resp.nextUrl,
    });
  }

  _loadSalesActivitiesRequest(nextUrl: string, filters: Filters = {}): Observable<RawSalesActivity[]> {
    const host = this.host.host();
    return this.loggedInUserInfoService.loggedInUserInfo$.pipe(
      switchMap((userInfo) => {
        let url = this.rebuildNexUrl(nextUrl);
        let body = {};
        if (!nextUrl) {
          url = `${host}${SALES_ACTIVITY_URLS.GET}`;
          body = JSON.stringify(
            this.buildGetActivityBody(userInfo.partnerId, userInfo.marketId, userInfo.salespersonId, filters),
          );
        }
        return this.doAjaxCall(url, body);
      }),
      catchError(() => {
        this.salesActivities$$.error('error');
        return throwError(this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_NOT_LOADED'));
      }),
      map(this.extractData),
      take(1),
    );
  }

  rebuildNexUrl(nextUrl: string): string {
    if (!nextUrl) {
      return nextUrl;
    }
    const url = new URL(nextUrl);
    url.searchParams.forEach((value, name) => {
      if (value === 'None') {
        url.searchParams.delete(name);
      }
    });

    return url.toString();
  }

  likeActivity(
    accountGroupId: string,
    salesActivityId: string,
  ): Observable<{ data: { reactionUserName: string; reactionUserId: string; reactionType: string } }> {
    const host = this.host.host();
    const url = `${host}${SALES_ACTIVITY_URLS.REACT}`;
    return this.loggedInUserInfoService.loggedInUserInfo$.pipe(
      switchMap((userInfo) => {
        const body = {
          reactionType: 'like',
          accountGroupId: accountGroupId,
          salesActivityId: salesActivityId,
          userName: userInfo.userName,
        };
        if (userInfo.salespersonId) {
          body['salesPersonId'] = userInfo.salespersonId;
        }
        if (userInfo.salesManagerId) {
          body['salesManagerId'] = userInfo.salesManagerId;
        }
        return this.doAjaxCall(url, JSON.stringify(body));
      }),
    );
  }

  commentOnActivity(
    accountGroupId: string,
    salesActivityId: string,
    commentText: string,
  ): Observable<{ data: { commentUserName: string; commentProfilePictureUrl: string; commentText: string } }> {
    const host = this.host.host();
    const url = `${host}${SALES_ACTIVITY_URLS.COMMENT}`;
    return this.loggedInUserInfoService.loggedInUserInfo$.pipe(
      switchMap((userInfo) => {
        const body = {
          commentText: commentText,
          accountGroupId: accountGroupId,
          salesActivityId: salesActivityId,
          userName: userInfo.userName,
        };
        if (userInfo.salespersonId) {
          body['salesPersonId'] = userInfo.salespersonId;
        }
        if (userInfo.salesManagerId) {
          body['salesManagerId'] = userInfo.salesManagerId;
        }
        return this.doAjaxCall(url, JSON.stringify(body));
      }),
    );
  }

  public buildGetActivityBody(
    partnerId: string,
    marketId: string,
    currentSalespersonId: string,
    filters: Filters,
  ): GetSalesActivityRequestBody {
    const body: GetSalesActivityRequestBody = {
      partnerId: partnerId,
      marketId: marketId,
      accountGroupId: filters.accountGroupId,
      currentSalespersonId: currentSalespersonId,
      salespersonId: filters.salesperson,
      accountStatus: filters.status,
      actionTaken: filters.action,
      opportunityId: filters.opportunityId,
    };

    const fromDate: string = filters.fromDate,
      toDate: string = filters.toDate;

    body.salespersonId = filters.filteredSalespeople !== undefined ? filters.filteredSalespeople : filters.salesperson;

    if (fromDate && fromDate.length > 0) {
      const fromDateMoment = moment(parseInt(fromDate[0], 10));
      const fromDateStr = fromDateMoment.format('YYYY-MM-DDTHH:mm:ssZ');
      body.fromDateWithTimezone = fromDateStr;
    }

    if (toDate && toDate.length > 0) {
      // Add one day to toDate so the filter is inclusive
      const toDateMoment = moment(parseInt(toDate[0], 10)).add(1, 'days');
      const toDateStr = toDateMoment.format('YYYY-MM-DDTHH:mm:ssZ');
      body.toDateWithTimezone = toDateStr;
    }

    return body;
  }

  public extractData(res: any): any {
    if (res.status < 200 || res.status >= 300) {
      throw new Error(this.translate.instant('SALES_ACTIVITY.BAD_RESPONSE_STATUS') + res.status);
    }
    return res;
  }

  public deserializeActivity = (rawItem: any): SalesActivity => {
    // this needs to be a lexical closure to keep a reference to `this`
    // https://stackoverflow.com/questions/20627138/typescript-this-scoping-issue-when-called-in-jquery-callback/20627988#20627988
    // alternative is writing our own decorator https://github.com/JohnWeisz/BoundMethods/blob/master/src/bound.ts
    return SalesActivity.buildSalesActivityFromRaw(rawItem, this.translate);
  };

  private doAjaxCall(url: string, body: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.post(url, body, { headers: headers, withCredentials: true }).pipe(map(this.extractData));
  }
}
